import React, { useState, useEffect } from "react";
import { ListValue } from "../../api/customerFieldApi";
import { Button } from "../../components/tailwind/button/Button";

interface InternalListValue extends ListValue {
  _id: string;
}

interface Props {
  value: ListValue[] | null;
  onChange: (values: ListValue[]) => void;
}

let idCounter = 0;
const generateId = (): string => `list-value-${++idCounter}`;

const toInternalValues = (values: ListValue[] | null): InternalListValue[] => {
  return (values || []).map((v) => ({ ...v, _id: generateId() }));
};

const toExternalValues = (values: InternalListValue[]): ListValue[] => {
  return values.map(({ value, label }) => ({ value, label }));
};

export const ListValuesEditorInline: React.FC<Props> = ({
  value,
  onChange,
}) => {
  const [editValues, setEditValues] = useState<InternalListValue[]>(() =>
    toInternalValues(value)
  );

  useEffect(() => {
    const externalValues = value || [];
    setEditValues((prev) => {
      if (prev.length !== externalValues.length) {
        return toInternalValues(value);
      }
      return prev.map((item, index) => ({
        ...item,
        value: externalValues[index]?.value || item.value,
        label: externalValues[index]?.label || item.label,
      }));
    });
  }, [value]);

  const handleValueChange = (
    id: string,
    field: "value" | "label",
    newValue: string
  ): void => {
    const updated = editValues.map((v) =>
      v._id === id ? { ...v, [field]: newValue } : v
    );
    setEditValues(updated);
    onChange(toExternalValues(updated));
  };

  const handleAddValue = (): void => {
    const updated = [
      ...editValues,
      { value: "", label: "", _id: generateId() },
    ];
    setEditValues(updated);
    onChange(toExternalValues(updated));
  };

  const handleDeleteValue = (id: string): void => {
    const updated = editValues.filter((v) => v._id !== id);
    setEditValues(updated);
    onChange(toExternalValues(updated));
  };

  const hasIncompleteValues = editValues.some(
    (v) => !v.value.trim() || !v.label.trim()
  );

  return (
    <div className="tw-space-y-4">
      <div className="tw-space-y-2">
        {editValues.length > 0 && (
          <div className="tw-flex tw-gap-3 tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-px-1">
            <div className="tw-flex-1">Value</div>
            <div className="tw-flex-1">Label</div>
            <div className="tw-w-8"></div>
          </div>
        )}

        {editValues.map((item) => (
          <div key={item._id} className="tw-flex tw-items-center tw-gap-3">
            <input
              type="text"
              value={item.value}
              onChange={(e) =>
                handleValueChange(item._id, "value", e.target.value)
              }
              placeholder="e.g. material_cotton"
              className={`tw-flex-1 tw-rounded-md tw-shadow-sm tw-text-sm tw-py-1.5 tw-px-2 ${
                !item.value.trim()
                  ? "tw-border-red-300 tw-ring-1 tw-ring-red-300 focus:tw-border-red-500 focus:tw-ring-red-500"
                  : "tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500"
              }`}
            />
            <input
              type="text"
              value={item.label}
              onChange={(e) =>
                handleValueChange(item._id, "label", e.target.value)
              }
              placeholder="e.g. Cotton"
              className={`tw-flex-1 tw-rounded-md tw-shadow-sm tw-text-sm tw-py-1.5 tw-px-2 ${
                !item.label.trim()
                  ? "tw-border-red-300 tw-ring-1 tw-ring-red-300 focus:tw-border-red-500 focus:tw-ring-red-500"
                  : "tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500"
              }`}
            />
            <button
              onClick={() => handleDeleteValue(item._id)}
              className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
              title="Delete value"
            >
              <i className="material-icons tw-text-lg">close</i>
            </button>
          </div>
        ))}

        {editValues.length === 0 && (
          <div className="tw-text-sm tw-text-gray-500 tw-py-2">
            No values yet. Click &quot;Add value&quot; to add one.
          </div>
        )}
      </div>

      <div>
        <Button variant="secondary" size="sm" onClick={handleAddValue}>
          <i className="material-icons tw-text-sm tw-mr-1">add</i>
          Add value
        </Button>
      </div>

      {hasIncompleteValues && (
        <div className="tw-text-xs tw-text-red-500">
          All values and labels must be filled in before saving.
        </div>
      )}
    </div>
  );
};
